import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useTenantCapabilities } from "@/hooks/useTenantCapabilities";
import {
  decideModuleAccess,
  getModuleForPath,
  getModuleUnavailableRecovery,
} from "@/lib/moduleRoutes";
import { getTenantModuleLabel } from "@/lib/tenantModuleMeta";

interface Props {
  /** Explicit module key. When omitted, it is derived from the current path. */
  moduleKey?: string;
  children: ReactNode;
}

function ModuleLoading() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function ModuleUnavailable({
  moduleKey,
  pathname,
}: {
  moduleKey: string;
  pathname?: string;
}) {
  const recovery = getModuleUnavailableRecovery(moduleKey, pathname);
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <Card className="max-w-md w-full">
        <CardHeader className="space-y-2">
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
            <Lock className="h-5 w-5 text-muted-foreground" />
          </div>
          <CardTitle>Module unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {getTenantModuleLabel(moduleKey)} is not enabled for your organisation, so
            this area is hidden. Contact your administrator if you need access.
          </p>
          {recovery && (
            <Button asChild variant="outline">
              <Link to={recovery.to}>{recovery.label}</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * V3 frontend-only module enforcement. Fail-open: unknown tenant, loading and
 * query errors never block. This is UX gating, not security.
 */
export function ModuleAccessGuard({ moduleKey, children }: Props) {
  const location = useLocation();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const { rows, isLoading, error } = useTenantCapabilities();

  const resolvedKey = moduleKey ?? getModuleForPath(location.pathname) ?? undefined;
  if (!resolvedKey) return <>{children}</>;

  // Wait for the tenant to settle so a disabled module never flashes its content
  // and an enabled one never flashes the denial screen.
  if (tenantLoading) return <ModuleLoading />;

  const decision = decideModuleAccess({
    moduleKey: resolvedKey,
    tenantKnown: !!currentTenant?.id,
    isLoading,
    rows,
    hasError: !!error,
  });

  if (decision === "loading") return <ModuleLoading />;
  if (decision === "blocked") return <ModuleUnavailable moduleKey={resolvedKey} pathname={location.pathname} />;
  return <>{children}</>;
}

export default ModuleAccessGuard;
